import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

// Your Supabase project ref:
const PROJECT_REF = 'wqagrmomwzikqpxwiskl';
const PREFIX = `sb-${PROJECT_REF}-auth-token`;
const FLOW_STATE_KEY = `${PREFIX}-flow-state`;
const CODE_VERIFIER_KEY = `${PREFIX}-code-verifier`;

// Only on native platforms
if (Capacitor.isNativePlatform()) {
  // If a working sessionStorage already exists, don’t override it.
  try {
    const testKey = '__pkce_test__';
    window.sessionStorage.setItem(testKey, '1');
    window.sessionStorage.removeItem(testKey);
    // It works — nothing to do.
    console.log('[PKCE] Native sessionStorage is writable; no polyfill needed.');
  } catch {
    // Need a polyfill
    const cache = new Map<string, string>();

    // Preload the two PKCE keys we care about
    (async () => {
      const [flow, verifier] = await Promise.all([
        Preferences.get({ key: FLOW_STATE_KEY }),
        Preferences.get({ key: CODE_VERIFIER_KEY }),
      ]);
      if (flow.value) cache.set(FLOW_STATE_KEY, flow.value);
      if (verifier.value) cache.set(CODE_VERIFIER_KEY, verifier.value);

      console.log('[PKCE] sessionStorage polyfill ready. flow-state:',
        cache.has(FLOW_STATE_KEY) ? 'FOUND' : 'NOT FOUND',
        'code-verifier:', cache.has(CODE_VERIFIER_KEY) ? 'FOUND' : 'NOT FOUND'
      );
    })();

    // Minimal Storage impl
    const poly: Storage = {
      get length() { return cache.size; },
      clear() { cache.clear(); /* don’t nuke Preferences blindly */ },
      getItem(key: string) { return cache.has(key) ? cache.get(key)! : null; },
      key(index: number) { return Array.from(cache.keys())[index] ?? null; },
      removeItem(key: string) {
        cache.delete(key);
        Preferences.remove({ key }).catch(() => {});
      },
      setItem(key: string, value: string) {
        cache.set(key, value);
        Preferences.set({ key, value }).catch(() => {});
      },
    };

    // Define a **getter** instead of assigning (read-only in WKWebView)
    try {
      Object.defineProperty(window, 'sessionStorage', {
        configurable: true,
        get: () => poly,
      });
      console.log('[PKCE] sessionStorage polyfilled via getter.');
    } catch (e) {
      // As a last resort, expose on global and let our code read it if needed
      (window as any).__sessionStoragePolyfill = poly;
      console.warn('[PKCE] Could not define window.sessionStorage; attached __sessionStoragePolyfill', e);
    }
  }
}
